import { getLanguageCapability } from './languageCapabilityRegistry';

export const CODE_EDITOR_PREFS_KEY = 'jarvisCodeEditorPreferences';

export type CodeEditorWordWrap = 'on' | 'off' | 'wordWrapColumn' | 'bounded';
export type CodeEditorRenderWhitespace = 'none' | 'boundary' | 'selection' | 'trailing' | 'all';

export type CodeEditorLanguageOverride = {
  tabSize?: number;
  insertSpaces?: boolean;
  fontSize?: number;
  wordWrap?: CodeEditorWordWrap;
};

export type CodeEditorPreferences = {
  fontSize: number;
  fontFamily: string;
  tabSize: number;
  insertSpaces: boolean;
  minimap: boolean;
  wordWrap: CodeEditorWordWrap;
  renderWhitespace: CodeEditorRenderWhitespace;
  formatOnSave: boolean;
  lintOnChange: boolean;
  languageOverrides: Record<string, CodeEditorLanguageOverride>;
};

export type EffectiveCodeEditorOptions = {
  fontSize: number;
  fontFamily: string;
  tabSize: number;
  insertSpaces: boolean;
  minimap: boolean;
  wordWrap: CodeEditorWordWrap;
  renderWhitespace: CodeEditorRenderWhitespace;
  formatOnSave: boolean;
  lintOnChange: boolean;
};

export const DEFAULT_CODE_EDITOR_PREFERENCES: CodeEditorPreferences = {
  fontSize: 14,
  fontFamily: "Consolas, 'Courier New', monospace",
  tabSize: 2,
  insertSpaces: true,
  minimap: true,
  wordWrap: 'off',
  renderWhitespace: 'selection',
  formatOnSave: false,
  lintOnChange: true,
  languageOverrides: {}
};

function storageAvailable(): boolean {
  return typeof localStorage !== 'undefined';
}

function clampFontSize(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_CODE_EDITOR_PREFERENCES.fontSize;
  return Math.min(32, Math.max(10, Math.round(value)));
}

function clampTabSize(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_CODE_EDITOR_PREFERENCES.tabSize;
  return Math.min(8, Math.max(1, Math.round(value)));
}

function isWordWrap(value: unknown): value is CodeEditorWordWrap {
  return value === 'on' || value === 'off' || value === 'wordWrapColumn' || value === 'bounded';
}

function isRenderWhitespace(value: unknown): value is CodeEditorRenderWhitespace {
  return (
    value === 'none' ||
    value === 'boundary' ||
    value === 'selection' ||
    value === 'trailing' ||
    value === 'all'
  );
}

function normalizeOverrides(
  raw: unknown
): Record<string, CodeEditorLanguageOverride> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, CodeEditorLanguageOverride> = {};
  for (const [languageId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const entry = value as Record<string, unknown>;
    const override: CodeEditorLanguageOverride = {};
    if (typeof entry.tabSize === 'number') override.tabSize = clampTabSize(entry.tabSize);
    if (typeof entry.insertSpaces === 'boolean') override.insertSpaces = entry.insertSpaces;
    if (typeof entry.fontSize === 'number') override.fontSize = clampFontSize(entry.fontSize);
    if (isWordWrap(entry.wordWrap)) override.wordWrap = entry.wordWrap;
    if (Object.keys(override).length > 0) {
      out[languageId] = override;
    }
  }
  return out;
}

export function normalizeCodeEditorPreferences(raw: unknown): CodeEditorPreferences {
  const base = { ...DEFAULT_CODE_EDITOR_PREFERENCES, languageOverrides: {} };
  if (!raw || typeof raw !== 'object') return base;

  const input = raw as Partial<CodeEditorPreferences>;
  return {
    fontSize: typeof input.fontSize === 'number' ? clampFontSize(input.fontSize) : base.fontSize,
    fontFamily:
      typeof input.fontFamily === 'string' && input.fontFamily.trim()
        ? input.fontFamily
        : base.fontFamily,
    tabSize: typeof input.tabSize === 'number' ? clampTabSize(input.tabSize) : base.tabSize,
    insertSpaces: typeof input.insertSpaces === 'boolean' ? input.insertSpaces : base.insertSpaces,
    minimap: typeof input.minimap === 'boolean' ? input.minimap : base.minimap,
    wordWrap: isWordWrap(input.wordWrap) ? input.wordWrap : base.wordWrap,
    renderWhitespace: isRenderWhitespace(input.renderWhitespace)
      ? input.renderWhitespace
      : base.renderWhitespace,
    formatOnSave: typeof input.formatOnSave === 'boolean' ? input.formatOnSave : base.formatOnSave,
    lintOnChange: typeof input.lintOnChange === 'boolean' ? input.lintOnChange : base.lintOnChange,
    languageOverrides: normalizeOverrides(input.languageOverrides)
  };
}

export function loadCodeEditorPreferences(): CodeEditorPreferences {
  if (!storageAvailable()) return { ...DEFAULT_CODE_EDITOR_PREFERENCES, languageOverrides: {} };
  try {
    const raw = localStorage.getItem(CODE_EDITOR_PREFS_KEY);
    if (!raw) return { ...DEFAULT_CODE_EDITOR_PREFERENCES, languageOverrides: {} };
    return normalizeCodeEditorPreferences(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_CODE_EDITOR_PREFERENCES, languageOverrides: {} };
  }
}

export function saveCodeEditorPreferences(prefs: CodeEditorPreferences): void {
  if (!storageAvailable()) return;
  try {
    const normalized = normalizeCodeEditorPreferences(prefs);
    localStorage.setItem(CODE_EDITOR_PREFS_KEY, JSON.stringify(normalized));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Merge global prefs with language indentation defaults and per-language overrides.
 * Indentation: language override → language capability default → global prefs.
 */
export function getEffectiveOptions(languageId: string): EffectiveCodeEditorOptions {
  const prefs = loadCodeEditorPreferences();
  const capability = getLanguageCapability(languageId);
  const override = prefs.languageOverrides[languageId] ?? {};

  return {
    fontSize: override.fontSize ?? prefs.fontSize,
    fontFamily: prefs.fontFamily,
    tabSize: override.tabSize ?? capability.indentation.tabSize ?? prefs.tabSize,
    insertSpaces: override.insertSpaces ?? capability.indentation.insertSpaces ?? prefs.insertSpaces,
    minimap: prefs.minimap,
    wordWrap: override.wordWrap ?? prefs.wordWrap,
    renderWhitespace: prefs.renderWhitespace,
    formatOnSave: prefs.formatOnSave,
    lintOnChange: prefs.lintOnChange
  };
}
